"use client";

import { useState } from "react";
import { ArrowLeft, ClipboardCheck, ArrowRight } from "lucide-react";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import { CompetenceInterviewGuide } from "@/components/tools/leadership/CompetenceInterviewGuide";
import { LockScreen } from "@/components/tools/leadership/LockScreen";
import { useLanguage } from "@/context/LanguageContext";

export default function InterviewPage() {
    const [isTestMode, setIsTestMode] = useState(false);
    const { t } = useLanguage();
    // Security: Token is kept in memory only. Refreshing the page will lock the assessment again.
    const [token, setToken] = useState("");
    const isUnlocked = !!token;

    if (isTestMode) {
        return (
            <div className="min-h-screen flex flex-col">
                <SEO
                    title="Assessment In Progress - Agile Coach Competence Test"
                    description="Interactive competence assessment scenarios."
                    keywords={["Agile Coach Competence Test", "Competence Assessment", "Scenarios"]}
                />
                <main id="main-content" className="container mx-auto px-4 py-8 flex-1">
                    <div className="flex items-center justify-between mb-8">
                        <Button variant="ghost" onClick={() => setIsTestMode(false)}>
                            <ArrowLeft className="mr-2 h-4 w-4" />
                            Exit Assessment
                        </Button>
                        <div className="flex items-center gap-2 text-muted-foreground font-medium">
                            <ClipboardCheck className="h-5 w-5" />
                            <span>Interactive Mode</span>
                        </div>
                    </div>

                    <div className="max-w-6xl mx-auto h-[min(800px,calc(100vh-140px))]">
                        {!isUnlocked ? (
                            <LockScreen onUnlock={(t) => setToken(t)} />
                        ) : (
                            <CompetenceInterviewGuide token={token} />
                        )}
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen">
            <SEO
                title={t("seo.leadership.interview.title")}
                description={t("seo.leadership.interview.description")}
                keywords={t("seo.leadership.interview.keywords").split(",").map(k => k.trim())}
                type="software"
                applicationCategory="EducationalApplication"
                operatingSystem="Web"
                price="0"
                currency="EUR"
            />
            <main id="main-content" className="container mx-auto px-4 py-12">
                <Link to="/tools/leadership_roles">
                    <Button variant="ghost" className="mb-8">
                        <ArrowLeft className="mr-2 h-4 w-4" />
                        Back to Leadership Roles
                    </Button>
                </Link>

                <div className="max-w-5xl mx-auto">
                    <div className="flex items-center gap-4 mb-8">
                        <div className="p-3 bg-primary/10 rounded-xl">
                            <ClipboardCheck className="h-8 w-8 text-primary" />
                        </div>
                        <h1 className="text-4xl font-bold">Agile Coach Competence Test</h1>
                    </div>

                    <div className="space-y-12 mb-12">
                        <section className="space-y-6">
                            <h2 className="text-2xl font-bold">Foundational Concept: How Competence is Developed</h2>
                            <p className="text-muted-foreground leading-relaxed max-w-3xl text-lg">
                                We often mistake <strong>Knowledge</strong> (books, workshops) for <strong>Competence</strong> (ability to act).
                                However, true Action Competence is a layered development process that goes far beyond just knowing "how it works".
                            </p>

                            <div className="border rounded-xl overflow-hidden bg-background my-6 shadow-sm max-w-4xl">
                                <img
                                    src="/images/competence_model.png"
                                    alt="Diagram showing the nested layers of competence development: Knowledge -> Skills -> Values -> Action Competence."
                                    className="w-full h-auto object-cover"
                                />
                                <div className="p-3 bg-muted/30 text-xs text-center text-muted-foreground border-t">
                                    Based on the model by Jost Buschmeyer (2015).
                                </div>
                            </div>

                            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 pt-4">
                                <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                                    <h4 className="font-bold text-orange-600 dark:text-orange-400 mb-2">1. Knowledge</h4>
                                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Teach & Inform</p>
                                    <p className="text-sm">"I know how it works."</p>
                                </div>
                                <div className="p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
                                    <h4 className="font-bold text-cyan-600 dark:text-cyan-400 mb-2">2. Skills</h4>
                                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Train & Practice</p>
                                    <p className="text-sm">"I can do it."</p>
                                </div>
                                <div className="p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                                    <h4 className="font-bold text-green-600 dark:text-green-400 mb-2">3. Values</h4>
                                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Experience</p>
                                    <p className="text-sm">"It matters to me."</p>
                                </div>
                                <div className="p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                                    <h4 className="font-bold text-blue-600 dark:text-blue-400 mb-2">4. Action</h4>
                                    <p className="text-xs text-muted-foreground uppercase tracking-wide mb-2">Complex Situations</p>
                                    <p className="text-sm">"I assume responsibility."</p>
                                </div>
                            </div>
                        </section>

                        <div className="bg-muted/50 p-6 rounded-xl border border-border/50">
                            <h3 className="text-lg font-semibold mb-3">Why this isn't a multiple-choice test</h3>
                            <p className="text-muted-foreground leading-relaxed">
                                Standard multiple-choice tests are excellent for checking <strong>Knowledge</strong> ("Do you recall the definition?").
                                However, they fail completely at predicting whether a person will succeed in <strong>novel, complex situations</strong> where there is no textbook answer.
                            </p>
                            <p className="text-muted-foreground leading-relaxed mt-4">
                                The following competence assessment is designed to test exactly that <strong>Action Competence</strong>.
                                It provides scenarios that force you to apply your knowledge in ways that require judgment, experience, and the right attitude—things that cannot be memorized.
                            </p>
                        </div>

                        <div className="flex flex-col items-center justify-center py-8 space-y-6">
                            <div className="relative group">
                                <div className="absolute -inset-1 bg-gradient-to-r from-primary to-blue-600 rounded-lg blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
                                <Button
                                    size="lg"
                                    onClick={() => setIsTestMode(true)}
                                    className="relative h-16 px-10 text-xl font-bold bg-background text-foreground border hover:bg-muted/50 transition-all"
                                >
                                    Start Competence Assessment
                                    <ArrowRight className="ml-3 h-6 w-6" />
                                </Button>
                            </div>
                            <p className="text-sm text-muted-foreground">Ready to test your skills? Click to begin the scenario-based assessment.</p>
                        </div>
                    </div>
                </div>
            </main>
            <Footer />
        </div >
    );
}
